import type { FormNode } from '@/designer/types/form.types'

export type ListButtonAction = 'create' | 'edit' | 'delete' | 'close' | 'search'

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase()
}

export function resolveListFormButtonAction(
  node: Pick<FormNode, 'name' | 'title' | 'role'>,
): ListButtonAction | undefined {
  if (node.role === 'custom' || node.role === 'aux') return undefined
  const key = norm(node.name || '')
  const tit = norm(node.title || '')

  if (['create', 'add', 'new', 'creer', 'nouveau'].includes(key)) return 'create'
  if (['edit', 'modify', 'change', 'modifier'].includes(key)) return 'edit'
  if (['delete', 'remove', 'supprimer'].includes(key)) return 'delete'
  if (['close', 'cancel', 'exit', 'fermer', 'annuler'].includes(key)) return 'close'
  if (['search', 'find', 'rechercher'].includes(key)) return 'search'

  if (tit === 'rechercher') return 'search'
  return undefined
}

export function isListSearchField(node: FormNode): boolean {
  return node.type === 'InputField' && norm(node.name) === 'listsearch'
}

export function hasListCrudCommandBar(root: FormNode[]): boolean {
  for (const n of root) {
    if (n.type !== 'CommandBar') continue
    for (const c of n.children ?? []) {
      if (c.type === 'Button' && resolveListFormButtonAction(c) === 'create') return true
    }
  }
  return false
}

export interface ListFormRuntimeActions {
  create: () => void
  edit: () => void
  delete: () => void
  close: () => void
  searchFocus?: () => void
  disabled?: boolean
  editDisabled?: boolean
  deleteDisabled?: boolean
}

export function resolveListButtonClick(
  node: FormNode,
  actions: ListFormRuntimeActions,
): (() => void) | undefined {
  const act = resolveListFormButtonAction(node)
  if (!act) return undefined
  switch (act) {
    case 'create':
      return actions.create
    case 'edit':
      return actions.editDisabled ? undefined : actions.edit
    case 'delete':
      return actions.deleteDisabled ? undefined : actions.delete
    case 'close':
      return actions.close
    case 'search':
      return actions.searchFocus
    default:
      return undefined
  }
}

export function isListButtonDisabled(
  node: FormNode,
  actions: ListFormRuntimeActions,
): boolean {
  if (actions.disabled) return true
  const act = resolveListFormButtonAction(node)
  if (act === 'edit') return !!actions.editDisabled
  if (act === 'delete') return !!actions.deleteDisabled
  return false
}
